import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { Button, Grid, IGridProps, KeepProportions, LoadingSpinner, SvgIcon } from '@gik/ui';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import arrayMove from 'array-move';
import { GiftCardTile } from './GiftCardTile';
import noop from '@gik/core/utils/noop';
import { Product } from '@gik/core/models/gik/Product';
import { isEqual } from '@gik/core/utils/validator';
import { useTranslation } from 'react-i18next';
import { translationKeys } from './i18n/en';
import { IGiftCard } from '@gik/calendar/models/GiftCard';

const calendarEventDisclaimerAcceptedKey = 'gik-calendar-event-disclaimer-accepted';

export interface IGiftCardsPickerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value?: Product[];
  loading?: boolean;
  disabled?: boolean;
  onChange?: (value: Product[]) => void;
  onEdit?: (selection: Product[]) => void;
}

function _GiftCardsPicker({
  children,
  value = [],
  className,
  loading,
  disabled,
  onChange = noop,
  onEdit = noop,
  ...otherProps
}: IGiftCardsPickerProps): React.ReactElement {
  const bem = bemBlock('gift-card-picker');
  const { t } = useTranslation();

  const [calendarEventDisclaimerAccepted, setCalendarEventDisclaimerAccepted] = React.useState<boolean>(
    localStorage.getItem(calendarEventDisclaimerAcceptedKey) === '1'
  );

  const idProp = 'id';

  const [selection, setSelection] = React.useState<Product[]>(value);

  function onSortEnd({ oldIndex, newIndex }) {
    const newSel = arrayMove(selection, oldIndex, newIndex);
    setSelection(newSel);
    onChange(newSel);
  }

  function handleDelete(item: IGiftCard) {
    const newSel = selection.filter(option => option[idProp] !== item[idProp]);
    setSelection(newSel);
    onChange(newSel);
  }

  function handleDismissFooterMessage() {
    localStorage.setItem(calendarEventDisclaimerAcceptedKey, '1');
    setCalendarEventDisclaimerAccepted(true);
  }

  React.useEffect(() => {
    if (!value) {
      return;
    }
    setSelection(value);
    //eslint-disable-next-line
  }, [value]);

  const maxCards = 4;
  let emptyCardsCount = maxCards;
  if (selection) {
    emptyCardsCount = maxCards - selection?.length;
  }

  const SortableGrid = SortableContainer<IGridProps>(Grid);
  const SortableItem = SortableElement(({ giftCard }: { giftCard: Product }) => {
    return (
      <GiftCardTile
        giftcard={giftCard}
        className={bem('drag-item')}
        deleteButton={selection?.length > 1}
        onDelete={handleDelete}
      />
    );
  });

  return (
    <div className={bem(null, [{ disabled }], className)} {...otherProps}>
      {loading ? (
        <LoadingSpinner center />
      ) : (
        <>
          <header className={bem('header')}>
            <span className={bem('title')}>{t(translationKeys.pickerTitle)}</span>
            <nav className={bem('toolbar')}>
              <Button pill variant="secondary" onClick={() => onEdit(selection)}>
                {t(translationKeys.pickerEditButton)}
              </Button>
            </nav>
          </header>
          <main className={bem('main')}>
            <SortableGrid distance={10} axis="xy" onSortEnd={onSortEnd}>
              {selection?.map((item, index: number) => {
                return <SortableItem key={index} index={index} giftCard={item} />;
              })}
              {emptyCardsCount > 0 &&
                [...Array(emptyCardsCount)].map((_value, count: number) => {
                  return (
                    <div key={'empty-' + count} className={bem('empty-tile')} onClick={() => onEdit(selection)}>
                      <main>
                        <KeepProportions>
                          <SvgIcon size="xl" name="PlusCircle" />
                        </KeepProportions>
                      </main>
                      <footer>hidden</footer>
                    </div>
                  );
                })}
            </SortableGrid>
          </main>
          {!calendarEventDisclaimerAccepted && (
            <footer className={bem('footer')}>
              {t(translationKeys.pickerDisclaimer)}{' '}
              <Button variant="primary-link" onClick={handleDismissFooterMessage}>
                {t(translationKeys.pickerDisclaimerDismiss)}
              </Button>
            </footer>
          )}
          {children}
        </>
      )}
    </div>
  );
}

// only re-render image if the value array changed
export const GiftCardsPicker = React.memo(_GiftCardsPicker, (prevProps, nextProps) => {
  if (prevProps.disabled !== nextProps.disabled) {
    return false;
  }
  if (prevProps.loading !== nextProps.loading) {
    return false;
  }
  if (prevProps.className !== nextProps.className) {
    return false;
  }

  if (!isEqual(prevProps.value, nextProps.value)) {
    return false;
  }

  return true;
});
